import { useMemo } from 'react';
import { useSharedNow } from '@/lib/infra/sharedClock';
import {
  classifyVenueFreshness,
  type VenueFreshness,
  type VenueRawStatus,
} from './venueFreshness';

/**
 * React wrapper over the pure freshness classifier (#125): ticks so the age
 * advances and a venue crosses its stale threshold live, without the caller
 * re-rendering. The classification itself stays in the pure module — this only
 * owns the clock. The tick comes off the shared 5s cadence (#333), so every
 * venue badge on the page rides one timer instead of one each.
 */
export function useVenueFreshness(
  status: VenueRawStatus,
  lastSuccessMs: number | null,
  staleThresholdMs: number | null,
  tickMs = 5_000,
): VenueFreshness {
  // Only tick when time can change the outcome: a pending age-threshold crossing
  // (staleThresholdMs set) or a currently-shown age label (status !== 'ready').
  // A fresh venue with no age threshold (Kalshi while ready) never ticks — the
  // reviewer's #158 ask, and it keeps this cheap wherever the hook is mounted.
  // Subscribing re-stamps the shared clock, so re-arming still refreshes the
  // age immediately rather than on the next tick.
  const needsTick = staleThresholdMs != null || status !== 'ready';
  const nowMs = useSharedNow(tickMs, needsTick);
  return useMemo(
    () => classifyVenueFreshness({ status, lastSuccessMs, nowMs, staleThresholdMs }),
    [status, lastSuccessMs, nowMs, staleThresholdMs],
  );
}
